import * as React from "react";
import { useCallback, useState, useContext } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import BootstrapModalConfirm from "components/bootstrap/BootstrapModalConfirm";
import { SortableList, IconButton } from "components/common";
import TitleTypes from "views/stores/TitleTypes";
import EditableTitle from "views/components/common/EditableTitle";
import DashboardPageContext from "views/components/contexts/DashboardPageContext";
import FindNewActiveQueryId from "views/logic/views/FindNewActiveQuery";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { setQueriesOrder, mergeQueryTitles } from "views/logic/slices/viewSlice";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import ConfirmDeletingDashboardPage from "views/logic/views/ConfirmDeletingDashboardPage";
import useWidgetIds from "views/components/useWidgetIds";
const ListItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
  overflow: hidden;
`;
const ListItem = ({
  item: { id, title },
  onRemove,
  onUpdateTitle,
  disableDelete
}) => /* @__PURE__ */ React.createElement(ListItemContainer, null, /* @__PURE__ */ React.createElement(
  EditableTitle,
  {
    key: title,
    disabled: !onUpdateTitle,
    value: title,
    onChange: (newTitle) => onUpdateTitle(id, newTitle)
  }
), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(IconButton, { title: `Remove page ${title}`, name: "delete", onClick: () => onRemove(id), disabled: disableDelete })));
const AdaptableQueryTabsConfiguration = ({ show, setShow, queriesList, activeQueryId, dashboardId }) => {
  const { setDashboardPage } = useContext(DashboardPageContext);
  const widgetIds = useWidgetIds();
  const [nextQueriesList, setNextQueriesList] = useState(queriesList);
  const disablePageDelete = nextQueriesList.size <= 1;
  const dispatch = useViewsDispatch();
  const sendTelemetry = useSendTelemetry("dashboard");
  const onConfirmPagesConfiguration = useCallback(() => {
    const isActiveQueryDeleted = !nextQueriesList.find(({ id }) => id === activeQueryId);
    sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_PAGE_CONFIGURATION_UPDATED, {
      app_pathname: "dashboard",
      app_action_value: "dashboard-page-configuration"
    });
    if (isActiveQueryDeleted) {
      const indexedQueryIds = queriesList.map(({ id }) => id).toList();
      const nextQueryIds = nextQueriesList.map(({ id }) => id).toArray();
      const removedQueryIds = indexedQueryIds.filter((queryId) => !nextQueryIds.includes(queryId)).toList();
      const newActiveQueryId = FindNewActiveQueryId(indexedQueryIds, activeQueryId, removedQueryIds);
      setDashboardPage(newActiveQueryId);
    }
    dispatch(setQueriesOrder(nextQueriesList.map(({ id }) => id).toOrderedSet())).then(() => {
      const newTitles = nextQueriesList.map(({ id, title }) => {
        const titleMap = Immutable.Map({ title });
        const titlesMap = Immutable.Map({
          [TitleTypes.Tab]: titleMap
        });
        return { queryId: id, titlesMap };
      }).toArray();
      dispatch(mergeQueryTitles(newTitles));
      setShow(false);
    });
  }, [nextQueriesList, sendTelemetry, dispatch, activeQueryId, queriesList, setDashboardPage, setShow]);
  const onPagesConfigurationModalClose = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_PAGE_CONFIGURATION_CANCELED, {
      app_pathname: "dashboard",
      app_action_value: "dashboard-page-configuration"
    });
    setShow(false);
  }, [sendTelemetry, setShow]);
  const updatePageSorting = useCallback(
    (order) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_PAGE_CONFIGURATION_SORTING_UPDATED, {
        app_pathname: "dashboard",
        app_action_value: "dashboard-page-configuration-sorting"
      });
      setNextQueriesList(Immutable.OrderedSet(order));
    },
    [sendTelemetry, setNextQueriesList]
  );
  const onUpdateTitle = useCallback((id, title) => {
    setNextQueriesList(
      (currentQueries) => currentQueries.map((query) => query.id === id ? { id, title } : query).toOrderedSet()
    );
  }, []);
  const removePage = useCallback(
    async (queryId) => {
      if (disablePageDelete) {
        return Promise.resolve();
      }
      if (await ConfirmDeletingDashboardPage(dashboardId, activeQueryId, widgetIds)) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_ACTION.DASHBOARD_PAGE_CONFIGURATION_PAGE_REMOVED, {
          app_pathname: "dashboard",
          app_action_value: "dashboard-page-configuration-remove-page"
        });
        setNextQueriesList((currentQueries) => currentQueries.filter((query) => query.id !== queryId).toOrderedSet());
      }
      return Promise.resolve();
    },
    [activeQueryId, dashboardId, disablePageDelete, sendTelemetry, widgetIds]
  );
  const customListItemRender = useCallback(
    // eslint-disable-next-line react/no-unused-prop-types
    ({ item }) => /* @__PURE__ */ React.createElement(ListItem, { item, onUpdateTitle, onRemove: removePage, disableDelete: disablePageDelete }),
    [disablePageDelete, removePage, onUpdateTitle]
  );
  return /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: show,
      title: "Update Dashboard Pages Configuration",
      onConfirm: onConfirmPagesConfiguration,
      onCancel: onPagesConfigurationModalClose,
      confirmButtonText: "Update configuration"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h3", null, "Order"), /* @__PURE__ */ React.createElement("p", null, "Use drag and drop to change the order of the dashboard pages. Double-click on a dashboard title to change it."), /* @__PURE__ */ React.createElement(
      SortableList,
      {
        items: nextQueriesList.toArray(),
        onMoveItem: updatePageSorting,
        displayOverlayInPortal: true,
        alignItemContent: "center",
        customContentRender: customListItemRender
      }
    ))
  );
};
export default AdaptableQueryTabsConfiguration;
